/**
 * Pure presentation helpers for service-request tasks.
 *
 * Centralises the status → StatusBadge variant mapping and urgency mapping so
 * every card renders status/urgency with a TEXT label (never colour alone) and
 * a consistent semantic colour.
 */

import type { ServiceRequest } from '@hostesa/api-client';
import type { StatusVariant } from '@hostesa/ui';

export type TaskStatus = ServiceRequest['status'];
export type TaskUrgency = ServiceRequest['urgency'];
export type TaskCategory = ServiceRequest['category'];
export type TaskSource = ServiceRequest['sourceChannel'];

/** Map a service-request status to a StatusBadge variant (colour is supplementary). */
export function statusVariant(status: TaskStatus): StatusVariant {
  switch (status) {
    case 'new':
      return 'pending';
    case 'assigned':
      return 'active';
    case 'in_progress':
      return 'online';
    case 'done':
      return 'success';
    case 'cancelled':
      return 'neutral';
    case 'escalated':
      return 'error';
    default:
      return 'neutral';
  }
}

/** Map urgency to a StatusBadge variant. */
export function urgencyVariant(urgency: TaskUrgency): StatusVariant {
  switch (urgency) {
    case 'high':
      return 'warning';
    case 'medium':
      return 'pending';
    case 'low':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/** Statuses that are terminal — no further actions are offered. */
const TERMINAL: ReadonlySet<TaskStatus> = new Set<TaskStatus>(['done', 'cancelled']);

export function isTerminal(status: TaskStatus): boolean {
  return TERMINAL.has(status);
}

export function isActive(status: TaskStatus): boolean {
  return !isTerminal(status);
}

/** Safely read a `nullableUnknown` string field (room, assignedTeam, …). */
export function asOptionalString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}
